'use client'
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Paper,
  Fade,
  Box,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

interface ModalTemplateProps {
  show: boolean;
  onClose: () => void;
  modalType?: "error" | "info" | "success";
  title: string;
  msg?: string;
  children?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  disableEscapeKeyDown?: boolean;
  hideCloseButton?: boolean;
  customIcon?: React.ReactNode;
  contentSx?: any;
}

const getModalTypeConfig = (modalType: ModalTemplateProps["modalType"]) => {
  switch (modalType) {
    case "error":
      return {
        icon: <ErrorIcon />,
        color: "error.main" as const,
        backgroundColor: "error.light" as const,
      };
    case "success":
      return {
        icon: <SuccessIcon />,
        color: "success.main" as const,
        backgroundColor: "success.light" as const,
      };
    case "info":
    default:
      return {
        icon: <InfoIcon />,
        color: "info.main" as const,
        backgroundColor: "info.light" as const,
      };
  }
};

function ModalTemplate({
  show,
  onClose,
  modalType = "info",
  title,
  msg,
  children,
  maxWidth = "xs",
  fullWidth = true,
  disableEscapeKeyDown = false,
  hideCloseButton = false,
  customIcon,
  contentSx,
}: ModalTemplateProps) {
  const theme = useTheme();
  const config = getModalTypeConfig(modalType);
  const icon = customIcon || config.icon;

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth={false}
      disableEscapeKeyDown={disableEscapeKeyDown}
      container={() => document.getElementById("app-root")}
      TransitionComponent={Fade}
      TransitionProps={{
        timeout: 300,
      }}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 2,
          overflow: "visible",
          width: "600px",
          minHeight: "300px",
        },
      }}
      aria-labelledby="modal-title"
      aria-describedby={msg ? "modal-description" : undefined}
    >
      {/* Header with colored background */}
      <DialogTitle
        id="modal-title"
        sx={{
          bgcolor: config.backgroundColor,
          color: "common.white",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: hideCloseButton ? 3 : 1,
          position: "relative",
        }}
      >
        <Box
          sx={{ color: "common.white", display: "flex", alignItems: "center" }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: "common.white",
          }}
        >
          {title}
        </Typography>
        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "common.white",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          pt: contentSx?.pt ?? 3,
          pb: contentSx?.pb ?? (children ? 2 : 3),
          px: contentSx?.px ?? 3,
          display: contentSx?.display ?? "flex",
          flexDirection: contentSx?.flexDirection ?? "column",
          justifyContent: contentSx?.justifyContent ?? "center",
          alignItems: contentSx?.alignItems ?? "center",
          textAlign: contentSx?.textAlign ?? "center",
          minHeight: contentSx?.minHeight ?? "200px",
          ...contentSx,
        }}
      >
        {msg && (
          <Typography
            id="modal-description"
            variant="body1"
            color="text.primary"
            sx={{
              mb: children ? 2 : 0,
              lineHeight: 1.6,
            }}
          >
            {msg}
          </Typography>
        )}
      </DialogContent>

      {/* Actions */}
      {children && (
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1,
            justifyContent: "center",
          }}
        >
          {children}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ModalTemplate;
export type { ModalTemplateProps };
